import React, { useEffect, useState } from "react";
import { Row, Col, Table, Button } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatIST, textCapitalize } from "../../Helpers/Utils";

const CustomerHistory = () => {

  const dispatch = useDispatch();
  const user = useSelector(s => s.customer.customer);
  const loading = useSelector(state => state.loading.loading);
  const { customer_id } = useParams();

  const [customer, setCustomer] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);

  const [today, setToday] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  const loadHistory = async () => {
    dispatch(loadingTrue());

    const params = {
      customer_id,
      outlet_id: user?.outlet_id
    };

    if (today) params.today = 1;
    if (dateFrom) params.date_from = dateFrom.toISOString().slice(0, 10);
    if (dateTo) params.date_to = dateTo.toISOString().slice(0, 10);

    const qs = new URLSearchParams(params).toString();

    const res = await Handler({
      method: "get",
      url: `/reports/customer_history.php?${qs}`
    });

    dispatch(loadingFalse());
    if (!res?.success) return toast.error(res?.msg);

    setCustomer(res.data.customer);
    setSummary(res.data.summary);
    setRows(res.data.transactions || []);
  };

  useEffect(() => {
    if (customer_id) loadHistory();
  }, [customer_id, today, dateFrom, dateTo]);

  return (
    <div style={{ padding: 0, background: "#f5f7fb" }}>

      {/* ===== HEADER ===== */}
      {customer && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontWeight: 700 }}>{textCapitalize(customer.name)}</h3>
          <span style={{ color: "#6b7280" }}>
            {customer.phone} • {customer.outlet}
          </span>
        </div>
      )}

      {/* ===== FILTER BAR (REACT DATE PICKER) ===== */}
      <div style={{ display: "flex", gap: 12, marginBottom: 24, alignItems: "center", justifyItems: "stretch" }}>
        <DatePicker
          selected={dateFrom}
          onChange={date => {
            setToday(false);
            setDateFrom(date);
          }}
          placeholderText="From date"
          className="form-control"
          dateFormat="dd-MM-yyyy"
          maxDate={new Date()}
        />

        <DatePicker
          selected={dateTo}
          onChange={date => {
            setToday(false);
            setDateTo(date);
          }}
          placeholderText="To date"
          className="form-control"
          dateFormat="dd-MM-yyyy"
          minDate={dateFrom}
        />

        <Button disabled={loading}
          style={{ background: today ? "#0f172a" : "#2563eb", border: "none", color: "#fff", padding: "6px 24px", height: 38 }}
          onClick={() => { setDateFrom(null); setDateTo(null); setToday(t => !t); }}>
          Today
        </Button>
      </div>

      {/* ===== COLOR DASHBOARD CARDS ===== */}
      {summary && (
        <Row className="g-4 mb-4 justify-content-between">
          <DashCard title="Total Sales" value={summary.sale_total} bg="#fb923c" />
          <DashCard title="Returns" value={summary.return_total} bg="#ef4444" />
          <DashCard title="Net Sales" value={summary.net_amount} bg="#0f172a" />
          <DashCard title="Collections" value={summary.collections} bg="#16a34a" />
          <DashCard title="Outstanding" value={summary.outstanding} bg="#2563eb" />
        </Row>
      )}

      {/* ===== BILLING STYLE TABLE ===== */}
      <div style={{
        background: "#fff",
        borderRadius: 14,
        boxShadow: "0 8px 24px rgba(0,0,0,.08)",
        overflow: "hidden"
      }}>
        <Table responsive hover className="mb-0 cust-report-table">
          <thead style={{
            background: "#f8fafc",
            borderBottom: "1px solid #e5e7eb"
          }}>
            <tr style={{ fontSize: 13 }}>
              <th>Date</th>
              <th>Invoice</th>
              <th>Type</th>
              <th className="text-end">Total</th>
              <th className="text-end">Paid</th>
              <th>Mode</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody style={{ fontSize: 14 }}>
            {rows.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center text-muted py-4">
                  No transactions found
                </td>
              </tr>
            )}

            {rows && rows?.map((r, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td>
                  {/* {new Date(r.created_at).toLocaleDateString()} */}
                  {formatIST(r?.created_at)}
                </td>
                <td style={{ fontWeight: 600 }}>{r.invoice_no}</td>
                <td>
                  <span style={{
                    padding: "4px 12px",
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    color: r.type === "RETURN" ? "#991b1b" : "#065f46",
                    background: r.type === "RETURN" ? "#fee2e2" : "#d1fae5"
                  }}>
                    {r.type}
                  </span>
                </td>
                <td className="text-end">₹ {Math.abs(r.total_amount).toFixed(2)}</td>
                <td className="text-end">₹ {Number(r.paid_amount).toFixed(2)}</td>
                <td>{r.payment_mode || "-"}</td>
                <td style={{ color: "#6b7280" }}>{r.note || "-"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

/* ===== DASHBOARD COLOR CARD ===== */
const DashCard = ({ title, value, bg }) => (
  <Col md={2}>
    <div style={{
      background: bg,
      borderRadius: 18,
      padding: 20,
      color: "#fff",
      minHeight: 120,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      boxShadow: "0 12px 30px rgba(0,0,0,.2)"
    }}>
      <div style={{ fontSize: 14, opacity: .9 }}>{title}</div>
      <div style={{ fontSize: 28, fontWeight: 700 }}>₹ {value}</div>
    </div>
  </Col>
);

export default CustomerHistory;
